/**
 * Resolves a localized string from a value that can be either a direct string
 * or a record containing translations for different locales (e.g., { ar: "...", en: "..." }).
 *
 * @param value The value to resolve.
 * @param locale The current active locale (e.g., 'ar', 'en').
 * @param fallback A fallback string if the value is null or unresolved.
 * @returns The resolved localized string.
 */
export function resolveLocalizedValue(
  value: unknown,
  locale: string,
  fallback = ""
): string {
  if (!value) {
    return fallback;
  }

  if (typeof value === "string") {
    return value.trim();
  }

  if (typeof value === "object" && value !== null) {
    const record = value as Record<string, unknown>;
    const localized = record[locale];

    if (typeof localized === "string") {
      return localized.trim();
    }

    // Fallback to other available keys if the current locale is missing
    const firstAvailable = Object.values(record).find((v) => typeof v === "string");
    if (typeof firstAvailable === "string") {
      return firstAvailable.trim();
    }
  }

  return fallback;
}
